import { useState, useEffect } from 'react';
import { Table, Tag, Spin, Descriptions } from 'antd';
import { formatDate } from '../../../helpers/genericHelper';
import API from '../../../services/API';
import usePageState from '../../../helpers/usePageState';

function RecentEmails({ userId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = usePageState('recent-emails-rows', []);

  const fetchRecentEmails = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const response = await API.getIterableEmails(userId);
      const eventsWithKey = (response || []).map((event, index) => ({
        ...event,
        key: index,
      }));
      setEvents(eventsWithKey);
    } catch (error) {
      console.error('Error fetching recent emails:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentEmails();
  }, [userId]);

  const columns = [
    {
      title: 'Event Type',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (eventType) => (
        <Tag color="blue">{eventType || '–'}</Tag>
      )
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => formatDate(date, true)
    },
    {
      title: 'Campaign',
      dataIndex: 'campaignName',
      key: 'campaignName',
      render: (name, record) => (
        <span>
          {name || '–'}
          {record.campaignId && <small> ID: {record.campaignId}</small>}
        </span>
      )
    },
    {
      title: 'Template',
      dataIndex: 'templateName',
      key: 'templateName',
      render: (name, record) => (
        <span>
          {name || '–'}
          {record.templateId && <small> ID: {record.templateId}</small>}
        </span>
      )
    },
    {
      title: 'Subject',
      dataIndex: 'emailSubject',
      key: 'emailSubject',
      render: (subject) => subject || '–'
    },
    {
      title: 'Channel ID',
      dataIndex: 'channelId',
      key: 'channelId',
      render: (id) => id || '–'
    },
    {
      title: 'Experiment ID',
      dataIndex: 'experimentId',
      key: 'experimentId',
      render: (id) => id || '–'
    },
    {
      title: 'Labels',
      dataIndex: 'labels',
      key: 'labels',
      render: (labels) => {
        if (!labels || labels.length === 0) return '–';
        return (
          <span>
            {labels.map((label, index) => (
              <Tag key={index} size="small">{label}</Tag>
            ))}
          </span>
        );
      }
    },
    {
      title: 'Transactional Data',
      dataIndex: 'transactionalData',
      key: 'transactionalData',
      render: (data) => {
        if (!data) return '–';
        return (
          <span style={{ 
            maxWidth: 150, 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            display: 'inline-block',
            cursor: 'pointer'
          }}>
            {data.substring(0, 30)}...
          </span>
        );
      }
    }
  ];

  const handleExpand = (expanded, record) => {
    if (expanded) {
      setExpandedRowKeys([record.key]);
    } else {
      setExpandedRowKeys([]);
    }
  };

  const expandedRowRender = (record) => {
    return (
      <Descriptions bordered column={1} size="small">
        {record.transactionalData && (
          <Descriptions.Item label="Transactional Data">
            <pre style={{ 
              margin: 0, 
              whiteSpace: 'pre-wrap', 
              wordBreak: 'break-word',
              maxHeight: '300px',
              overflow: 'auto'
            }}>
              {record.transactionalData}
            </pre>
          </Descriptions.Item>
        )}
      </Descriptions>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Table
      dataSource={events}
      columns={columns}
      rowKey={(record) => record.key}
      pagination={{ pageSize: 10 }}
      expandable={{
        expandedRowRender,
        expandedRowKeys,
        onExpand: handleExpand,
      }}
      locale={{
        emptyText: 'No recent email events found'
      }}
    />
  );
}

export default RecentEmails;
